
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, Check, X, MapPin, Mail, Phone, Globe, Facebook, Instagram, FileText } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCategories } from '@/hooks/useCategories';
import { getBusinessStatus } from '@/utils/businessUtils';
import BusinessSheetHeader from './BusinessSheetHeader';

interface BusinessReviewSheetProps {
  businessId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const BusinessReviewSheet = ({ 
  businessId, 
  isOpen, 
  onClose 
}: BusinessReviewSheetProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const { data: categoriesData } = useCategories();
  
  const { data: business, isLoading, error } = useQuery({
    queryKey: ['business', businessId],
    queryFn: async () => {
      if (!businessId) return null;
      
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id, 
          name, 
          description,
          contact_email,
          contact_phone,
          website,
          address_line1,
          address_line2,
          city,
          county,
          postal_code,
          country,
          facebook_url,
          instagram_url,
          tiktok_url,
          is_active,
          is_listed,
          created_at,
          category_id,
          user_id,
          community_id,
          application_comments,
          company_banner_url,
          company_logo,
          business_discounts (
            id,
            discount_amount,
            is_percentage,
            applies_to_all,
            description,
            is_active,
            status,
            type,
            coupon_code
          )
        `)
        .eq('id', businessId)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: !!businessId && isOpen
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      console.log(`Approving business ${id} via edge function`);
      
      const { data, error } = await supabase.functions.invoke('submit-business-approval', {
        body: { 
          business_id: id,
          is_approved: true
        }
      });
        
      if (error) {
        console.error("Error approving business:", error);
        throw error;
      }
      
      console.log(`Business approved successfully via edge function`);
      return { id, data };
    },
    onSuccess: async (data) => {
      toast({
        title: 'Business approved & listed',
        description: 'The business has been automatically approved and listed. An email notification has been sent.',
        variant: "default",
      });
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      queryClient.invalidateQueries({ queryKey: ['business', data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'business-stats'] });
      
      setIsSubmitting(false);
      onClose();
    },
    onError: (error: any) => {
      console.error("Approval error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve business",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, rejection_reason }: { id: string; rejection_reason?: string }) => {
      console.log(`Rejecting business ${id} with reason: ${rejection_reason || 'No reason provided'}`);
      
      const { data, error } = await supabase.functions.invoke('submit-business-approval', {
        body: { 
          business_id: id, 
          is_approved: false,
          rejection_reason 
        }
      });

      if (error) {
        console.error("Error rejecting business:", error);
        throw error;
      }
      
      console.log(`Business rejected successfully`);
      return { id };
    },
    onSuccess: async (data) => {
      toast({
        title: 'Business rejected',
        description: 'The business has been rejected.',
        variant: "default",
      });
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      queryClient.invalidateQueries({ queryKey: ['business', data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'business-stats'] });
      
      setIsSubmitting(false);
      setShowRejectDialog(false);
      setRejectionComment('');
      onClose();
    },
    onError: (error) => {
      console.error("Rejection error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject business",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  });

  // Find the category for the current business
  const getCategoryName = (categoryId: string): string => {
    if (!categoriesData?.categories) return 'Uncategorized';
    const category = categoriesData.categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const handleApprove = () => {
    if (!business) return;
    setIsSubmitting(true);
    // Approve business via edge function which handles permissions and emails
    approveMutation.mutate({ id: business.id });
  };

  const handleReject = () => {
    setShowRejectDialog(true);
  };

  const handleConfirmReject = () => {
    if (!business) return;
    setIsSubmitting(true);
    // Reject business via edge function which handles permissions and updates
    rejectMutation.mutate({ 
      id: business.id, 
      rejection_reason: rejectionComment.trim() || undefined 
    });
  };

  const handleCancelReject = () => {
    setShowRejectDialog(false);
    setRejectionComment('');
  };

  // Rejection Confirmation Dialog Component
  const RejectConfirmDialog = () => (
    <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Business Application</DialogTitle>
          <DialogDescription>
            Are you sure you want to reject this business application? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="rejection-comment">Reason for rejection (optional)</Label>
            <Textarea
              id="rejection-comment"
              placeholder="Provide a reason for rejecting this application..."
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleCancelReject}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirmReject}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Rejecting...' : 'Reject Business'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md w-full overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Business Review</SheetTitle>
          <SheetDescription>
            Review the business details before approving or rejecting
          </SheetDescription>
        </SheetHeader>
        
        {isLoading ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : error ? (
          <div className="py-6 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <p className="text-destructive">Failed to load business details</p>
          </div>
        ) : !business ? (
          <p className="py-6 text-center text-muted-foreground">No business selected</p>
        ) : (
          <div className="space-y-6">
            {/* Business Header with Banner and Logo */}
            <BusinessSheetHeader 
              business={business}
              categoryName={getCategoryName(business.category_id)}
              editMode={false}
            />
            
            {business.description && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Description</h4>
                <p className="text-sm">{business.description}</p>
              </div>
            )}
            
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Contact Information</h4>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{business.contact_email}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{business.contact_phone}</span>
                </div>
                {business.website && (
                  <a 
                    href={business.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-sm hover:text-primary transition-colors break-all"
                  >
                    <Globe className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                    <span className="text-xs">{business.website}</span>
                  </a>
                )}
              </div>
            </div>
            
            {(business.address_line1 || business.city) && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Address</h4>
                <div className="flex text-sm">
                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    {business.address_line1 && <p>{business.address_line1}</p>}
                    {business.address_line2 && <p>{business.address_line2}</p>}
                    {business.city && (
                      <p>
                        {business.city}
                        {business.county && `, ${business.county}`}
                        {business.postal_code && ` ${business.postal_code}`}
                      </p>
                    )}
                    {business.country && <p>{business.country}</p>}
                  </div>
                </div>
              </div>
            )}
            
            {(business.facebook_url || business.instagram_url || business.tiktok_url) && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Social Media</h4>
                <div className="space-y-2">
                  {business.facebook_url && (
                    <a 
                      href={business.facebook_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-sm hover:text-primary transition-colors break-all"
                    >
                      <Facebook className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                      <span className="text-xs">{business.facebook_url}</span>
                    </a>
                  )}
                  {business.instagram_url && (
                    <a 
                      href={business.instagram_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-sm hover:text-primary transition-colors break-all"
                    >
                      <Instagram className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                      <span className="text-xs">{business.instagram_url}</span>
                    </a>
                  )}
                  {business.tiktok_url && (
                    <a 
                      href={business.tiktok_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-sm hover:text-primary transition-colors break-all"
                    >
                      <svg
                        className="h-4 w-4 mr-2 text-muted-foreground shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
                        <path d="M15 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                        <path d="M15 2v20" />
                        <path d="M9 15v7" />
                        <path d="M15 13h-3a4 4 0 0 0-4 4" />
                      </svg>
                      <span className="text-xs">{business.tiktok_url}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {business.business_discounts && business.business_discounts.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Discount Information</h4>
                {business.business_discounts.map((discount) => (
                  <div key={discount.id} className="bg-muted/50 p-3 rounded-md space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Badge variant={discount.type === 'Free' ? 'secondary' : 'default'}>
                          {discount.type}
                        </Badge>
                        <Badge variant={discount.is_active ? 'outline' : 'destructive'}>
                          {discount.status}
                        </Badge>
                      </div>
                      <span className="text-lg font-semibold">
                        {discount.is_percentage 
                          ? `${discount.discount_amount}%` 
                          : `£${discount.discount_amount}`}
                        {discount.is_percentage ? ' off' : ' discount'}
                      </span>
                    </div>
                    {discount.description && (
                      <p className="text-sm text-muted-foreground">{discount.description}</p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span>Applies to: {discount.applies_to_all ? 'All services' : 'Specific services'}</span>
                      {discount.coupon_code && (
                        <span className="font-mono bg-background px-2 py-1 rounded">
                          Code: {discount.coupon_code}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {business.application_comments && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Application Comments</h4>
                <div className="flex text-sm">
                  <FileText className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="bg-muted/50 p-3 rounded-md w-full">
                    <p className="text-sm whitespace-pre-wrap">{business.application_comments}</p>
                  </div>
                </div>
              </div>
            )}
            
            <Separator />
            
            <div className="flex flex-col gap-2 pt-2">
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Review Decision</h4>
              <div className="flex gap-2">
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white" 
                  onClick={handleApprove}
                  disabled={isSubmitting}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve & List Business
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={handleReject}
                  disabled={isSubmitting}
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
      
      <RejectConfirmDialog />
    </Sheet>
  );
};
